// Points derivations for the gameweek grid (extracted verbatim from
// GameweekGrid.tsx). Kept pure and free of React so the live-total rules (which chip
// replaces vs. adds to the normal 5/3/1 scoring) can be unit tested directly.
import { applyMultipliers, isCorrectOutcome, scorePrediction } from '../../scoring'
import { computeCleanSweepPoints } from '../../season'
import { categoryForBasePoints, type RevealCategory } from '../../revealSequence'
import type { Game, Prediction } from '../../types'

export type ScoreLine = { homeScore: number; awayScore: number }

/** Shared "results visible so far" view — used by the live running total below plus the
 *  Goalfest/Clean Sweep live tickers, so all three always agree on which fixtures
 *  currently count. */
export function computeRevealedResults(
  games: Game[],
  actualResults: Record<string, ScoreLine>,
  isGameRevealed: (gameId: string) => boolean
): Record<string, ScoreLine> {
  return games.reduce<Record<string, ScoreLine>>((acc, game) => {
    const actual = isGameRevealed(game.id) ? actualResults[game.id] : undefined
    if (actual) acc[game.id] = actual
    return acc
  }, {})
}

/** Clean Sweep replaces the normal 5/3/1 scoring entirely (mutually exclusive with every
 * other chip) — `computeCleanSweepPoints` doubles as the live total here
 *  since it already treats not-yet-revealed fixtures as "skip", the same live-ticker
 *  shape Goalfest's bonus uses. */
export function computeGameweekPointsSoFar({
  games,
  predictions,
  revealedResults,
  bankerGameId,
  isDoubleUpActive,
  isGoalfestActive,
  isCleanSweepActive,
}: {
  games: Game[]
  predictions: Record<string, Prediction>
  revealedResults: Record<string, ScoreLine>
  bankerGameId: string | null
  isDoubleUpActive: boolean
  isGoalfestActive: boolean
  isCleanSweepActive: boolean
}): number {
  if (isCleanSweepActive) {
    return computeCleanSweepPoints(games, predictions, revealedResults, bankerGameId)
  }
  return games.reduce((sum, game) => {
    const actual = revealedResults[game.id]
    const prediction = predictions[game.id]
    if (!actual || !prediction) return sum

    const basePoints = scorePrediction(prediction, actual).points

    let points = applyMultipliers(basePoints, game.id === bankerGameId, isDoubleUpActive)

    if (
      isGoalfestActive &&
      !prediction.isAutoPredicted &&
      isCorrectOutcome(
        prediction.homeScore,
        prediction.awayScore,
        actual.homeScore,
        actual.awayScore
      )
    ) {
      points += actual.homeScore + actual.awayScore
    }

    return sum + points
  }, 0)
}

/** Result-category for a revealing card, recomputed at render from the same base-points
 *  tiering the conductor used — cheaper than threading the category through phase state
 *  and guaranteed to agree with GameCard's tier. */
export function revealCategoryFor(
  gameId: string,
  predictions: Record<string, Prediction>,
  actualResults: Record<string, ScoreLine>
): RevealCategory {
  const actual = actualResults[gameId]
  const prediction = predictions[gameId]
  const basePoints = actual && prediction ? scorePrediction(prediction, actual).points : 0
  return categoryForBasePoints(basePoints)
}
